/**
 * SignifyOrderStatusReport
 *
 * Presents the answer to an on-demand Signify order status check. Consumer:
 * `RenderActions`, inside the overlay it opens for a `pending` API record.
 *
 * The wording is the deliverable: each of the four cases states what it asserts
 * AND what it does not, because the check is observation only. A match does not
 * say this attempt placed the order, a no-match is not evidence the order was
 * never placed, a failed check asserts nothing at all, and in every case the log
 * entry is untouched and still reads as awaiting a response. `status` is an
 * opaque vendor string — shown verbatim, never branched on.
 *
 * A truncated search is said out loud in both answers it can reach: the check reads
 * one page, so a no-match drawn from a partial look must never read as
 * searched-and-absent, and a match drawn from one may be hiding a sibling order.
 */

import { FC } from 'react';
import { Alert, Typography } from 'antd';
import type { SignifyOrderStatusCheck } from '#types';
import FlexCol from '../../../../atoms/FlexCol';

const { Text } = Typography;

interface SignifyOrderStatusReportProps {
    check: SignifyOrderStatusCheck;
}

const UNCHANGED = 'Nothing was changed. This entry still reads as awaiting a response.';

const SignifyOrderStatusReport: FC<SignifyOrderStatusReportProps> = ({ check }) => {
    if (check.result === 'notSearchable') {
        return (
            <Alert
                type="warning"
                showIcon
                title={<Text strong>There is nothing to search on</Text>}
                description={
                    <FlexCol gap={5}>
                        <Text>{check.reason}</Text>
                        <Text>
                            No search was made, so nothing was learned either way. {UNCHANGED}
                        </Text>
                    </FlexCol>
                }
            />
        );
    }

    if (check.result === 'searchFailed') {
        return (
            <Alert
                type="error"
                showIcon
                title={<Text strong>The check did not complete</Text>}
                description={
                    <FlexCol gap={5}>
                        <Text>
                            Signify could not be asked, so nothing was learned either way. This is not an
                            answer about the order: it may exist, or it may not.
                        </Text>
                        <Text type="secondary">Reported: {check.failure}</Text>
                        <Text>{UNCHANGED} You can run the check again.</Text>
                    </FlexCol>
                }
            />
        );
    }

    if (check.result === 'noMatch') {
        return (
            <Alert
                type="warning"
                showIcon
                title={<Text strong>Signify returned no order under this reference</Text>}
                description={
                    <FlexCol gap={5}>
                        <Text>
                            Searched on purchase order number {check.searchedPurchaseOrderNumber}. Signify
                            returned {check.returnedCount} order
                            {check.returnedCount === 1 ? '' : 's'}, and none of them matched this entry.
                        </Text>
                        {check.truncated && (
                            <Text strong>
                                {check.reportedCount === undefined
                                    ? 'The search was cut short: this is one page of results, not all of them, so orders that were not read could still match.'
                                    : `The search was cut short: Signify says it holds ${check.reportedCount} orders under this number and only ${check.returnedCount} were read, so orders that were not read could still match.`}
                            </Text>
                        )}
                        <Text>
                            This says only that nothing matched in what the search returned. It is not
                            evidence that the order was never placed: how far back the search looks, and
                            which sales channels it covers, are not documented, so an order can exist and
                            still not appear here.
                        </Text>
                        <Text>{UNCHANGED}</Text>
                    </FlexCol>
                }
            />
        );
    }

    return (
        <Alert
            type="info"
            showIcon
            title={
                <Text strong>
                    {check.matches.length === 1
                        ? 'Signify has an order under this reference'
                        : `Signify has ${check.matches.length} orders under this reference`}
                </Text>
            }
            description={
                <FlexCol gap={10}>
                    {check.matches.map((match) => (
                        <FlexCol gap={5} key={match.orderNumber}>
                            <Text strong>
                                Order {match.orderNumber} — status {match.status}
                            </Text>
                            <Text type="secondary">
                                PO number {match.purchaseOrderNumber || '—'} · reference{' '}
                                {match.externalReference || '—'} · created {match.creationDate || '—'}
                            </Text>
                            <Text type="secondary">
                                {match.matchedOn === 'externalReference'
                                    ? 'Matched on the reference this submission sent.'
                                    : 'Matched on the purchase order number only, not on the reference this submission sent.'}
                            </Text>
                        </FlexCol>
                    ))}
                    <Text>
                        This says an order exists under this reference now. It does not say that this
                        attempt placed it: the reference is a purchase order number a person typed, so a
                        different submission carrying the same number produces this same result. Signify
                        returns a calendar date only, which cannot tell two attempts made on the same day
                        apart.
                    </Text>
                    {check.truncated && (
                        <Text strong>
                            {check.reportedCount === undefined
                                ? 'The search was cut short: this is one page of results, not all of them, so Signify may hold further orders under this number that are not listed here.'
                                : `The search was cut short: Signify says it holds ${check.reportedCount} orders under this number and only ${check.returnedCount} were read, so it may hold further orders that are not listed here.`}
                        </Text>
                    )}
                    <Text>{UNCHANGED}</Text>
                </FlexCol>
            }
        />
    );
};

export default SignifyOrderStatusReport;
